import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Download, ChevronLeft, ChevronRight, BookOpen } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PAGE_SIZE = 50;

// Format ZAR currency with signed sign for debits.
const fmtZar = (v) => {
  const num = Number(v || 0);
  const abs = Math.abs(num).toLocaleString('en-ZA', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
  return `${num < 0 ? '-' : ''}R ${abs}`;
};

// Deal Room: an investment in a new deal within the portfolio, not a
// withdrawal (2026-10-08). The stored type name is kept for existing rows.
const TYPE_NAMES = { deal_room_withdrawal: 'Deal Room Investment', deal_room_return: 'Deal Room Return' };
const humanType = (t) => TYPE_NAMES[t] || (t || '')
  .replace(/_/g, ' ')
  .replace(/\b\w/g, (c) => c.toUpperCase());

const typeColor = (t) => {
  const s = (t || '').toLowerCase();
  if (s.startsWith('deal_room')) return 'bg-violet-50 text-violet-700 border-violet-200';
  if (s.includes('deposit') || s.includes('investment') || s.includes('capital_in') || s === 'deal_purchase' || s === 'reinvestment') return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  if (s.includes('withdraw') || s.includes('redemption')) return 'bg-red-50 text-red-700 border-red-200';
  if (s.includes('interest') || s === 'deal_repayment') return 'bg-blue-50 text-blue-700 border-blue-200';
  if (s.includes('fee') || s === 'costs') return 'bg-amber-50 text-amber-700 border-amber-200';
  return 'bg-slate-50 text-slate-700 border-slate-200';
};

const LedgerPage = () => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchLedger = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), page_size: String(PAGE_SIZE) });
      if (fromDate) params.set('from_date', fromDate);
      if (toDate) params.set('to_date', toDate);
      if (typeFilter) params.set('txn_type', typeFilter);
      const r = await axios.get(`${API_URL}/api/ledger?${params}`, { headers });
      setRows(r.data.items || []);
      setTotal(r.data.total || 0);
      setPages(r.data.pages || 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load ledger');
    } finally {
      setLoading(false);
    }
  }, [page, fromDate, toDate, typeFilter, token]);

  useEffect(() => { fetchLedger(); }, [fetchLedger]);

  const exportCsv = () => {
    // Client-side CSV of currently displayed rows only. Full-export
    // is intentionally not offered here — investors get statements
    // (which are official) via the Statements page.
    const header = ['Date', 'Type', 'Amount (ZAR)', 'Loan', 'Borrower', 'Category', 'Reference', 'Description'];
    const csv = [header.join(',')].concat(
      rows.map(r => [
        r.date,
        humanType(r.type),
        r.amount,
        r.loan_id || '',
        (r.borrower_name || '').replace(/,/g, ';'),
        (r.category || '').replace(/,/g, ';'),
        (r.reference || '').replace(/,/g, ';'),
        (r.description || '').replace(/,/g, ';'),
      ].map(v => `"${String(v ?? '')}"`).join(','))
    ).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ledger-page-${page}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="ledger-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <BookOpen className="h-7 w-7 text-[#1D2D2A]" />
            <h1 className="text-3xl font-bold text-[#212529] font-['Manrope']">Ledger</h1>
          </div>
          <p className="text-[#6C757D] mt-1">
            Every reconciled transaction on your account. Unreconciled items are
            excluded and only appear here once your administrator has confirmed them.
          </p>
        </div>
        <Button variant="outline" onClick={exportCsv} data-testid="ledger-export-csv" disabled={rows.length === 0}>
          <Download className="h-4 w-4 mr-2" /> Export CSV
        </Button>
      </div>

      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Filters</CardTitle>
          <CardDescription>Narrow the view by date range or transaction type.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <label className="text-xs text-[#6C757D] block mb-1">From</label>
              <Input type="date" value={fromDate} onChange={e => { setFromDate(e.target.value); setPage(1); }} data-testid="ledger-from-date" />
            </div>
            <div>
              <label className="text-xs text-[#6C757D] block mb-1">To</label>
              <Input type="date" value={toDate} onChange={e => { setToDate(e.target.value); setPage(1); }} data-testid="ledger-to-date" />
            </div>
            <div>
              <label className="text-xs text-[#6C757D] block mb-1">Type</label>
              <select
                value={typeFilter}
                onChange={e => { setTypeFilter(e.target.value); setPage(1); }}
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                data-testid="ledger-type-filter"
              >
                <option value="">All types</option>
                <option value="deal_purchase">Deal Purchase</option>
                <option value="deal_repayment">Deal Repayment</option>
                <option value="investment">Investment</option>
                <option value="withdrawal">Withdrawal</option>
                <option value="bank_interest">Bank Interest</option>
                <option value="fee_mgmt">Management Fee</option>
                <option value="fee_perf">Performance Fee</option>
                <option value="costs">Costs</option>
              </select>
            </div>
            <div className="flex items-end">
              <Button
                variant="ghost"
                className="text-[#6C757D]"
                onClick={() => { setFromDate(''); setToDate(''); setTypeFilter(''); setPage(1); }}
                data-testid="ledger-clear-filters"
              >
                Clear filters
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-0 shadow-sm">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-28">Date</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Loan / Borrower</TableHead>
                  <TableHead className="hidden md:table-cell">Category</TableHead>
                  <TableHead className="hidden lg:table-cell">Reference</TableHead>
                  <TableHead className="text-right w-40">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  Array.from({ length: 8 }).map((_, i) => (
                    <TableRow key={`skel-${i}`}>
                      <TableCell colSpan={6}><Skeleton className="h-6 w-full" /></TableCell>
                    </TableRow>
                  ))
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-[#6C757D] py-12">
                      No reconciled transactions match your filters yet.
                    </TableCell>
                  </TableRow>
                ) : rows.map((r) => (
                  <TableRow key={r.id} data-testid={`ledger-row-${r.id}`}>
                    <TableCell className="font-mono text-xs">{r.date || '-'}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`${typeColor(r.type)} border font-normal`}>
                        {humanType(r.type)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {r.loan_id ? (
                        <div className="text-sm">
                          <div className="font-medium text-[#212529]">{r.borrower_name || r.loan_id}</div>
                          <div className="text-xs text-[#6C757D] font-mono">{r.loan_id}</div>
                        </div>
                      ) : (
                        <span className="text-xs text-[#ADB5BD]">—</span>
                      )}
                    </TableCell>
                    <TableCell className="hidden md:table-cell text-sm text-[#6C757D]">{r.category || '—'}</TableCell>
                    <TableCell className="hidden lg:table-cell text-xs text-[#6C757D] font-mono">{r.reference || '—'}</TableCell>
                    <TableCell className={`text-right font-semibold ${r.amount < 0 ? 'text-red-600' : 'text-emerald-700'}`}>
                      {fmtZar(r.amount)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
            <p className="text-sm text-[#6C757D]">
              {total > 0 ? (
                <>Showing page <strong>{page}</strong> of <strong>{pages}</strong> · {total} transactions</>
              ) : (
                'No transactions'
              )}
            </p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page === 1 || loading} onClick={() => setPage(p => Math.max(1, p - 1))} data-testid="ledger-prev-page">
                <ChevronLeft className="h-4 w-4 mr-1" /> Prev
              </Button>
              <Button variant="outline" size="sm" disabled={page >= pages || loading} onClick={() => setPage(p => p + 1)} data-testid="ledger-next-page">
                Next <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default LedgerPage;
